RivalIQ.apps.Dashboard.module('widgets', function (widgets, App, Backbone, Marionette, $, _, moment, d3) {

    'use strict';

    const TimeChart = RivalIQ.apps.Analyze.views.TimeChart;

    const BENCHMARK_LINE_ID = 'benchmarkLine';

    function getTooltipBreakdownMetrics(metrics) {

        const tooltipMetrics = _.reduce(metrics, function (memo, metric) {

            if (metric.benchmarkCompaniesFilter) {
                return memo;
            }

            if (!_.includes(metric.dimensions, 'channel')) {
                // This is a cross-channel metric, we can show a per-channel breakdown.
                memo.push(RivalIQ.metric.build(metric, {
                    dimensions: ['channel']
                }));
            } else if (_.includes(['avg_engagement_total_per_day', 'engagement_total'], metric.property) && !_.includes(metric.dimensions, 'engagement_type')) {
                // This is engagement total, we can show a per-engagement_type breakdown.
                memo.push(RivalIQ.metric.build(metric, {
                    dimensions: ['engagement_type']
                }));
            } else if (_.includes(['avg_engagement_total_per_day', 'engagement_total'], metric.property) &&
                metric.engagement_type === 'applause' &&
                metric.channel === 'facebook' &&
                !_.includes(metric.dimensions, 'reaction_type')) {
                // This is facebook applause, we can show a per-engagement_type breakdown.
                memo.push(RivalIQ.metric.build(metric, {
                    dimensions: ['reaction_type']
                }));
            } else if (_.includes(['all_count', 'avg_count_per_day'], metric.property) &&
                metric.channel === 'twitter' &&
                !_.includes(metric.dimensions, 'tweetType')) {
                // This is twitter count, we can show a per tweetType breakdown.
                memo.push(RivalIQ.metric.build(metric, {
                    dimensions: ['tweetType']
                }));
            }

            return memo;
        }, []);

        return tooltipMetrics;
    }

    const CompanyLine = TimeChart.extend({
        className: 'chart company-line block-chart text-center',

        dashboardModelEvents: {
            'change': 'reload'
        },

        minHeight: 425,

        loadData: function () {
            const me = this;

            const metricDefs = me.dashboardModel.metrics;
            const tooltipMetrics = getTooltipBreakdownMetrics(metricDefs);

            const metrics = new RivalIQ.collections.MetricDefinitions(metricDefs.concat(tooltipMetrics));

            const fetches = [
                metrics.fetch()
            ];

            const props = me.getProps();
            const focusCompanyOnlyLine = props.focusCompanyOnlyLine;
            const source = RivalIQ.metric.property.source(_.get(metrics.at(0), ['attributes']));
            if (focusCompanyOnlyLine && source && RivalIQ.enums.sourceDetails[source].privateData) {
                const fetchProps = _.defaults({}, props, {
                    companyId: props.focusCompanyId,
                    channel: RivalIQ.enums.sourceDetails[source].channel
                });
                fetches.push(RivalIQ.data.privateMetadata.fetch(fetchProps));
            }

            return $.when.apply($, fetches)
                .done(function (metrics, privateMetadata) {
                    me.tooltipMetrics = _.times(tooltipMetrics.length, function () {
                        return metrics.pop();
                    });

                    me.collection = metrics;

                    if (privateMetadata) {
                        const focusCompanyMetadata = _.find(privateMetadata.companies, {
                            id: props.focusCompanyId
                        });
                        me.showFocusCompanyNoPermsMessage = !focusCompanyMetadata || !_.get(focusCompanyMetadata, ['sources', source, 'validToken']);
                    } else {
                        me.showFocusCompanyNoPermsMessage = false;
                    }
                });
        },

        getTooltipSubmetrics: function () {
            const me = this;

            return _.reduce(me.tooltipMetrics, function (memo, tooltipMetric) {
                const data = tooltipMetric.getChartData();

                const lineIdx = RivalIQ.metric.freeDimensionIndex(me.dashboardModel.metrics[0], me.getProp('lineDimension'));

                let dataByCompanyId = _.groupBy(data, function (datum) {
                    if (tooltipMetric.get('benchmarkCompaniesFilter')) {
                        return BENCHMARK_LINE_ID;
                    }
                    return tooltipMetric.get('companyId') || datum[lineIdx];
                });

                const intervalDimension = RivalIQ.metric.timePeriod.getInterval(tooltipMetric.attributes);
                const intervalDimensionIdx = RivalIQ.metric.freeDimensionIndex(tooltipMetric.attributes, intervalDimension);
                const breakdownDimension = _.head(_.difference(tooltipMetric.getFreeDimensions(), RivalIQ.metric.getFreeDimensions(me.dashboardModel.metrics[0])));
                const breakdownDimensionIdx = RivalIQ.metric.freeDimensionIndex(tooltipMetric.attributes, breakdownDimension);
                const breakdownDimensionOrdering = tooltipMetric.getStaticDimensionValues(breakdownDimension);

                const valueFormatter = RivalIQ.metric.format.formatFn(tooltipMetric.attributes);
                const formattedDimensionsCache = {};

                dataByCompanyId = _.mapValues(dataByCompanyId, function (singleCompanyValues) {
                    let dataByDay = _.groupBy(singleCompanyValues, intervalDimensionIdx);
                    dataByDay = _.mapValues(dataByDay, function (dayValues) {

                        // order by breakdownDimensionOrdering so that each tooltip has data in the same order.
                        dayValues = _.orderBy(dayValues, function (dayValue) {
                            const breakdownValue = _.get(dayValue, [breakdownDimensionIdx]);
                            return _.indexOf(breakdownDimensionOrdering, breakdownValue);
                        });

                        // drop all the other values
                        dayValues = _.map(dayValues, function (dayValue) {

                            let formattedDimension = formattedDimensionsCache[dayValue[breakdownDimensionIdx]];
                            if (!formattedDimension) {
                                formattedDimension = formattedDimensionsCache[dayValue[breakdownDimensionIdx]] = tooltipMetric.formatDimension(breakdownDimension, dayValue[breakdownDimensionIdx]);
                            }

                            return {
                                key: formattedDimension,
                                value: valueFormatter(_.last(dayValue))
                            };
                        });

                        return dayValues;
                    });

                    return dataByDay;
                });

                return _.extend(memo, dataByCompanyId);
            }, {});

        },

        getMargins: function () {
            const me = this;

            const omitGraphTitle = me.getProp('omitGraphTitle');

            return {
                top: (omitGraphTitle ? 10 : 20),
                right: 0,
                bottom: (omitGraphTitle ? 0 : 10),
                left: 0
            };
        },
        onShow: _.noop,
        onAttachLoaded: function () {
            const me = this;

            me.width = me.$el.innerWidth();
            const metric = me.collection.at(0);
            const metricSource = metric.get('source');
            const chartData = metric.getChartData();
            if (me.showFocusCompanyNoPermsMessage) {
                const source = RivalIQ.metric.property.source(me.dashboardModel.metrics[0]);
                me.$el.html(Marionette.TemplateCache.get('#lineChartNotAvailableForFocusCompanyTemplate')({
                    sourceName: RivalIQ.enums.sourceDetails[source].name
                }));
            } else if (RivalIQ.enums.sourceDetails[metricSource].privateData && _.isEmpty(chartData)) {
                const View = RivalIQ.apps.Insights.views.getNoPresenceView(metricSource);
                const view = new View({
                    dashboardModel: me.dashboardModel,
                    el: me.el
                });
                view.render();

            } else {
                TimeChart.prototype.onShow.call(me);
            }

            TimeChart.prototype.onAttachLoaded.apply(me, arguments);
        },

        isContentLinkable: function () {
            const me = this;
            return me.collection.first().hasRelatedActivity();
        },

        isVolumetric: function () {
            const me = this;
            return me.collection.first().getMetricType() === RivalIQ.enums.metricType.VOLUME;
        },

        getTimeAnalysisMode: function () {
            const me = this;
            return me.dashboardModel.metrics[0].timeAnalysisMode;
        },
        getDataType: function () {
            const me = this;
            return RivalIQ.metric.property.type(me.dashboardModel.metrics[0]);
        },
        getLabelY: function () {
            const me = this;
            return RivalIQ.metric.names.metricName(me.dashboardModel.metrics[0], {
                dimensions: me.getProp('nameDimensions')
            });
        },
        getFormatX: function () {
            const me = this;
            const metric = me.collection.first();
            return function (value) {
                return metric.format(value);
            };
        },
        getPopoverFormatX: function () {

        },
        getYAxisFormat: function () {
            const me = this;
            const metric = me.collection.first();
            return function (value) {
                return metric.axisFormat(value, {
                    tick: true
                });
            };
        },
        getTickSize: function () {
            const me = this;
            const metric = me.dashboardModel.metrics[0];
            if (RivalIQ.metric.timePeriod.isByWeek(metric)) {
                return 'week';
            } else if (RivalIQ.metric.timePeriod.isByMonth(metric)) {
                return 'month';
            }
            return 'day';
        },
        getTimePeriod: function () {
            const me = this;
            const timePeriod = me.collection.first().get('timePeriod');
            return RivalIQ.utils.timePeriod.rehydrate(_.clone(timePeriod));
        },
        getDataDateRange: function () {
            const me = this;

            // NOTE clone to prevent modifying the base data dates
            const dateRollup = _.clone(me.getTimePeriod());
            const interval = me.getTickSize();

            // when we roll up for week/date we back up a bit, so adjust the start/end dates accordingly
            dateRollup.mainPeriodStart = moment.utc(dateRollup.mainPeriodStart).startOf(interval);
            // NOTE we adjust to EOD b/c the timeseries graph expects that and
            // rolls it back :/
            dateRollup.mainPeriodEnd = moment.utc(dateRollup.mainPeriodEnd).startOf(interval).endOf('day');

            return dateRollup;
        },

        createLineData: function (lineId, dimensionData) {
            const me = this;

            const startDate = moment.utc(me.getTimePeriod().mainPeriodStart).startOf(me.getTickSize());

            const values = _.map(dimensionData, _.last);

            return {
                id: lineId,
                hasData: Boolean(_.reject(values, _.isNull).length),
                isExcluded: _.includes(me.getProp('exclusions'), lineId),
                values: _.map(values, function (val, i) {
                    return {
                        // TODO guarenteed to be slooww
                        date: moment.utc(startDate).add(i, me.getTickSize()),
                        total: val
                    };
                }),
                maxValue: d3.max(values)
            };
        },

        getData: function () {
            const me = this;

            const metric = me.collection.at(0);
            const data = metric.getChartData();
            const lineIdx = RivalIQ.metric.freeDimensionIndex(me.dashboardModel.metrics[0], me.getProp('lineDimension'));

            const lines = _.groupBy(data, function (datum) {
                return datum[lineIdx];
            });

            let allLinesData;

            if (me.getProp('showBenchmarkLine')) {
                const focusCompanyId = Number(me.getProp('focusCompanyId'));
                const focusCompanyLineData = me.createLineData(focusCompanyId, data);
                const focusCompany = RivalIQ.state.landscape.get('companies').get(focusCompanyId);
                focusCompanyLineData.name = (focusCompany ? focusCompany.get('name') : '');

                let benchmarkLineData = null;

                const benchmarkMetric = me.collection.at(1);
                if (benchmarkMetric) {
                    benchmarkLineData = me.createLineData(BENCHMARK_LINE_ID, benchmarkMetric.getChartData());
                    benchmarkLineData.lineClass = 'benchmark-line';

                    const compareAgainst = _.find(RivalIQ.enums.benchmarkCompareAgainstOptions, {
                        aggregate: benchmarkMetric.get('aggregate'),
                        benchmarkCompaniesFilter: benchmarkMetric.get('benchmarkCompaniesFilter')
                    });
                    benchmarkLineData.compareAgainst = compareAgainst; // write this here so that the View Content modal can pass it to social posts later.

                    const benchmarkCompanies = RivalIQ.apps.Overview.utils.getCompaniesForFilter(benchmarkMetric.get('benchmarkCompaniesFilter'), me);
                    if (benchmarkCompanies.length === 1) {
                        // If we're just a single company, just use that company's name.
                        benchmarkLineData.name = benchmarkCompanies[0].get('name');
                    } else {
                        benchmarkLineData.name = _.get(compareAgainst, ['name'], 'Benchmark');
                    }

                    const calculationMetadata = benchmarkMetric.getMetadata().calculationMetadata;
                    const incompleteDataDetails = RivalIQ.utils.incompleteData.getBenchmarkIncompleteDataDetails(calculationMetadata, benchmarkMetric, compareAgainst, benchmarkMetric.get('dimensions'));
                    if (incompleteDataDetails.dataNotAvailable) {
                        benchmarkLineData.notAvailableTooltip = incompleteDataDetails.dataNotAvailableTooltip;
                    }
                }

                allLinesData = _.compact([
                    focusCompanyLineData,
                    benchmarkLineData
                ]);

            } else if (me.getProp('focusCompanyOnlyLine')) {
                const focusCompanyId = Number(me.getProp('focusCompanyId'));
                const focusCompanyLineData = me.createLineData(focusCompanyId, data);
                const focusCompany = RivalIQ.state.landscape.get('companies').get(focusCompanyId);
                focusCompanyLineData.name = (focusCompany ? focusCompany.get('name') : '');

                allLinesData = [focusCompanyLineData];
            } else {
                allLinesData = _.map(lines, function (dimensionData, lineValue) {

                    const companyId = Number(lineValue);
                    const lineData = me.createLineData(companyId, dimensionData);
                    const companies = RivalIQ.state.landscape.get('companies');
                    const company = (companies ? companies.get(companyId) : null);
                    const companyName = (company ? company.get('name') : '');
                    lineData.name = companyName;
                    return lineData;
                });
            }

            return allLinesData;
        },

        initialize: function () {
            const me = this;
            TimeChart.prototype.initialize.apply(me, arguments);
        }
    });

    const PanelWithGroupBy = RivalIQ.apps.Dashboard.widgets.ChartWidget.prototype.panelView.extend({

        showActions: function () {
            const me = this;

            me.showGroupByDropdown();

            RivalIQ.apps.Dashboard.widgets.ChartWidget.prototype.panelView.prototype.showActions.apply(me, arguments);
        },

        showGroupByDropdown: function () {
            const me = this;

            if (!me.dashboardModel.getCuration().isCustomDashboard) {
                me.actions.show(new RivalIQ.apps.Dashboard.views.IntervalDropdown({
                    dashboardModel: me.dashboardModel
                }), {
                    preventDestroy: true
                });
            }
        }
    });

    const CompanyLineWidget = RivalIQ.apps.Dashboard.widgets.ChartWidget.mixin({
        view: CompanyLine,

        mixins: {
            userSettingsChanged: RivalIQ.apps.Dashboard.mixins.UserSettingsChanged
        },

        panelView: PanelWithGroupBy,
        detailPanelView: PanelWithGroupBy,

        maxDimensionCount: _.constant(2),

        onUserSettingsChanged: function () {
            const me = this;

            const interval = RivalIQ.apps.Dashboard.views.IntervalDropdown.getGroupByInterval(me.getProps());
            me.setProps('interval', interval);
        },

        isSupported: function (props) {
            return Boolean(props.metrics) && props.metrics.length === 1 && RivalIQ.metric.graph.hasHistory(props.metrics[0]);
        },

        propValues: {
            timeAnalysisMode: [
                RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD,
                RivalIQ.enums.timeAnalysisMode.DELTA_ABSOLUTE,
                RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE
            ]
        },

        _getInterval: function (props) {
            const me = this;

            let interval = props.interval;
            if (!me.getCuration().isCustomDashboard) {
                if (!me.isExporting() || !interval) {
                    interval = RivalIQ.apps.Dashboard.views.IntervalDropdown.getGroupByInterval(props);
                }
            } else {
                interval ||= RivalIQ.metric.timePeriod.getDefaultInterval(me.getProp('timePeriod'));
            }

            return interval;
        },

        getChartTitle: function (dimensions) {
            const me = this;

            dimensions = dimensions || ['channel', 'timeAnalysisMode'];
            let title = RivalIQ.metric.names.metricName([me.metrics[0]], {
                dimensions: dimensions
            });

            if (!me.curation.isCustomDashboard) {
                const interval = me._getInterval(me.getProps());
                title += ` per ${_.upperFirst(interval)}`;
            } else if (me.getProp('addTimeIntervalToTitle')) {
                const metricInterval = RivalIQ.metric.timePeriod.getDefaultInterval(me.getProp('timePeriod'));
                title += ` / ${_.upperFirst(metricInterval)}`;
            }

            return title;
        },

        description: function () {
            const me = this;

            return me.getProp('description') || RivalIQ.metric.render.description(me.metrics[0], me.descriptionOptions);
        },

        processProps: function (props) {
            const me = this;

            _.defaults(props, {
                exclusions: [],
                lineDimension: 'companyId',
                nameDimensions: ['channel', 'timeAnalysisMode'],
                useUniversalMetricsApi: me.curation.isCustomDashboard
            });

            // We need to make a separate copy of the props as, if we override metrics in props here, then this line widget's metrics
            // will always be overridden. See #27154.
            let metricProps = props;
            const metric = _.get(metricProps, ['metrics', '0']);
            props.interval = me._getInterval(props);

            if (!me.getCuration().isCustomDashboard) {
                delete props.useAutoTimeInterval;
            }
            metricProps = _.defaults({
                metrics: [RivalIQ.metric.timePeriod.setInterval(metric, props.interval)]
            }, metricProps);
            me.metrics = me.buildMetrics(metricProps);

            const metricDefs = me.metrics;

            if (props.useAutoTimeInterval) {
                const metricInterval = RivalIQ.metric.timePeriod.getDefaultInterval(props.timePeriod);
                metricDefs[0] = RivalIQ.metric.timePeriod.setInterval(metricDefs[0], metricInterval);
            }

            if (props.showBenchmarkLine) {
                const selectedBenchmarkCompareAgainst = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(props.selectedBenchmarkCompareAgainst);
                const baseMetric = metricDefs[0];

                const numCompaniesInBenchmark = RivalIQ.apps.Overview.utils.getCompaniesForFilter(selectedBenchmarkCompareAgainst.benchmarkCompaniesFilter, me);
                const minCompaniesForBenchmark = (selectedBenchmarkCompareAgainst.benchmarkCompaniesFilter === RivalIQ.enums.benchmarkCompaniesFilters.exclude_focus_company.id ?
                    1 : 2);
                if (_.size(numCompaniesInBenchmark) >= minCompaniesForBenchmark) {
                    metricDefs.push(RivalIQ.metric.build(baseMetric, {
                        dimensions: _.without(baseMetric.dimensions || [], 'companyId'),
                        aggregate: selectedBenchmarkCompareAgainst.aggregate,
                        benchmarkCompaniesFilter: selectedBenchmarkCompareAgainst.benchmarkCompaniesFilter
                    }, {
                        clobberDimensions: true
                    }));
                }

                metricDefs[0] = RivalIQ.metric.build(metricDefs[0], {
                    companyId: props.focusCompanyId
                });

                props.showBottomLegend = true;
            } else {
                props.showBottomLegend = false;
            }

            return props;
        },

        propsToOmitFromSerialization: ['exclusions'],

        csvExportProps: function () {
            const me = this;

            let metrics = me.metrics;

            if (me.getProp('showBenchmarkLine')) {
                const selectedBenchmarkCompareAgainst = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(me.getProp('selectedBenchmarkCompareAgainst'));
                metrics = [
                    RivalIQ.metric.build(me.metrics[0], {
                        extraFreeDimensions: {
                            companyId: 'companyId'
                        }
                    }),
                    RivalIQ.metric.build(me.metrics[1], {
                        extraFreeDimensions: {
                            companyId: selectedBenchmarkCompareAgainst.name
                        },
                        useMatchingMetricColumn: true
                    })
                ];
            }

            const collection = new RivalIQ.collections.MetricDefinitions(metrics);

            return {
                type: 'metric',
                wideRow: true,
                nameDimensions: ['channel', 'timeAnalysisMode'],
                metric: collection.invoke('serialize')
            };
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.METRIC_COMPANY_LINE_CHART, CompanyLineWidget);

    const FocusCompanyLineWidget = CompanyLineWidget.extend({

        processProps: function (props, changed) {
            const me = this;

            props = CompanyLineWidget.prototype.processProps.apply(me, arguments);

            props.showBottomLegend = true;
            props.focusCompanyOnlyLine = true;
            const metricDefs = me.metrics;
            metricDefs[0] = RivalIQ.metric.build(metricDefs[0], {
                companyId: props.focusCompanyId
            });

            return props;
        },

        csvExportProps: function () {
            const me = this;

            let metrics = me.metrics;

            if (me.getProp('showBenchmarkLine')) {
                const selectedBenchmarkCompareAgainst = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(me.getProp('selectedBenchmarkCompareAgainst'));
                metrics = [
                    RivalIQ.metric.build(me.metrics[0], {
                        extraFreeDimensions: {
                            companyId: 'companyId'
                        }
                    }),
                    RivalIQ.metric.build(me.metrics[1], {
                        extraFreeDimensions: {
                            companyId: selectedBenchmarkCompareAgainst.name
                        },
                        useMatchingMetricColumn: true
                    })
                ];
            } else {
                metrics = [
                    RivalIQ.metric.build(me.metrics[0], {
                        extraFreeDimensions: {
                            companyId: 'companyId'
                        }
                    })
                ];
            }

            const collection = new RivalIQ.collections.MetricDefinitions(metrics);

            return {
                type: 'metric',
                wideRow: true,
                nameDimensions: ['channel', 'timeAnalysisMode'],
                metric: collection.invoke('serialize')
            };
        }
    });
    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.FOCUS_COMPANY_METRIC_LINE_CHART, FocusCompanyLineWidget);


}, window.moment, window.d3);
